import { useTranslation } from "react-i18next";
import type { FirestoreSemanticData } from "reedy-data/models";
import { BasePanel } from "components/BasePanel";
import { EmptyPanel } from "components/empty/EmptyPanel";
import { Icon } from "components/icon/Icon";
import { LoadingPanel, ErrorPanel } from "components/panels";
import { Text } from "components/text";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { CharactersSection } from "./CharactersSection";
import { OverviewSection } from "./OverviewSection";
import { SummarySection } from "./SummarySection";
import { WorldSettingSection } from "./WorldSettingSection";
import {
  charactersWithContent,
  getAvailableSummarySections,
  semanticDataLanguage,
  type SummarySection as SummarySectionName,
} from "./utils";

interface SummaryTabViewProps {
  productTitle: string;
  semanticData: FirestoreSemanticData | undefined;
  loading: boolean;
  error: Error | undefined;
}

export const SummaryTabView = ({
  productTitle,
  semanticData,
  loading,
  error,
}: SummaryTabViewProps) => {
  const { t } = useTranslation(["productDetails"]);
  const availableSections: SummarySectionName[] = getAvailableSummarySections(
    semanticData,
  ).filter(
    (section) =>
      section === "overview" ||
      section === "summary" ||
      section === "worldSetting" ||
      section === "characters",
  );

  if (loading) {
    return <LoadingPanel loadingText={t("productDetails:summary.loading")} />;
  }

  if (error) {
    return (
      <ErrorPanel
        title={t("productDetails:tabs.summary")}
        errorTitle={t("productDetails:summary.error.title")}
        errorMessage={t("productDetails:summary.error.message")}
      />
    );
  }

  if (!semanticData || availableSections.length === 0) {
    return (
      <BasePanel
        isEmpty
        emptyComponent={
          <EmptyPanel
            image={
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100">
                <Icon
                  name="file-text"
                  size="large"
                  iconClassName="text-secondary-400"
                />
              </div>
            }
            title={t("productDetails:summary.empty.title")}
            text={t("productDetails:summary.empty.text")}
            classes={{ container: "bg-secondary-50" }}
          />
        }
      />
    );
  }

  const sections = [
    {
      value: "overview" as const,
      label: t("productDetails:summary.sections.overview"),
    },
    {
      value: "summary" as const,
      label: t("productDetails:summary.sections.summary"),
    },
    {
      value: "worldSetting" as const,
      label: t("productDetails:summary.sections.worldSetting"),
    },
    {
      value: "characters" as const,
      label: t("productDetails:summary.sections.characters"),
    },
  ].filter((section) => availableSections.includes(section.value));
  const characters = charactersWithContent(semanticData.characters);
  const contentLanguage = semanticDataLanguage(semanticData);

  return (
    <BasePanel wrapper={false} className="h-full shadow-table">
      <Tabs
        key={`${semanticData.id}:${availableSections.join(",")}`}
        defaultValue={sections[0]?.value}
        orientation="vertical"
        className="min-h-0 flex-1 gap-0"
      >
        <nav
          aria-label={t("productDetails:summary.sections.title")}
          className="flex w-[260px] shrink-0 flex-col border-r border-secondary-200 bg-white"
        >
          <div className="flex h-12 shrink-0 items-center border-b border-secondary-200 bg-secondary-25 px-5">
            <Text variant="label">
              {t("productDetails:summary.sections.title")}
            </Text>
          </div>
          <TabsList
            aria-label={t("productDetails:summary.sections.title")}
            variant="line"
            className="w-full items-stretch gap-0 p-0"
          >
            {sections.map((section) => (
              <TabsTrigger
                key={section.value}
                value={section.value}
                className="min-h-10 w-full rounded-none border-y-0 border-r-0 border-l-2 border-l-transparent px-5 py-2 text-secondary-600 after:hidden data-[state=active]:border-l-primary-500 data-[state=active]:bg-secondary-50 data-[state=active]:text-secondary-800"
              >
                {section.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </nav>
        {availableSections.includes("overview") && (
          <TabsContent
            value="overview"
            className="h-full min-w-0 overflow-y-auto p-8"
          >
            <OverviewSection
              contentLanguage={contentLanguage}
              productTitle={productTitle}
              semanticData={semanticData}
            />
          </TabsContent>
        )}
        {availableSections.includes("summary") && (
          <TabsContent
            value="summary"
            className="h-full min-w-0 overflow-y-auto p-8"
          >
            <SummarySection
              contentLanguage={contentLanguage}
              paragraphHistory={semanticData.paragraphHistory ?? []}
            />
          </TabsContent>
        )}
        {availableSections.includes("worldSetting") &&
          semanticData.worldSettings && (
            <TabsContent
              value="worldSetting"
              className="h-full min-w-0 overflow-y-auto p-8"
            >
              <WorldSettingSection
                contentLanguage={contentLanguage}
                worldSettings={semanticData.worldSettings}
              />
            </TabsContent>
          )}
        {availableSections.includes("characters") && (
          <TabsContent
            value="characters"
            className="h-full min-w-0 overflow-y-auto p-8"
          >
            <CharactersSection
              characters={characters}
              contentLanguage={contentLanguage}
            />
          </TabsContent>
        )}
      </Tabs>
    </BasePanel>
  );
};
